import {
  basketQueryKeys,
  eventsQueryKeys,
  getEvent,
  getEventBySlug,
  useAuthGuest,
  useMyUserQuery,
} from "@ac-expo/api";
import {
  AuthGuestResponseDto,
  MyUserType,
  PublicEventDetails,
  TicketTypeItemType,
} from "@ac-expo/api-types";
import { Button, Input } from "@ac-expo/ui-components";
import { getErrorMessage } from "@ac-expo/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useUserContext } from "@web-app/src/context/auth/user.context";
import AuthStore from "@web-app/src/services/AuthStore";
import { isAxiosError } from "axios";
import { FormEvent, useEffect, useRef, useState } from "react";

// A question, not a promise: the flag says the event has Group tickets on sale, never that
// this visitor is a member. Only the unlock itself, server-side, settles that.
export const GROUP_UNLOCK_LINE = "Vous bénéficiez d'un tarif groupe ?";
export const GROUP_UNLOCK_NO_GROUP_MESSAGE =
  "Aucun tarif groupe n'est associé à cette adresse";
export const GROUP_TICKETS_TITLE = "Billets de votre groupe";

// The guest authentication answers 400 when the email belongs to a real account.
const ACCOUNT_EXISTS_HTTP_STATUS = 400;
const EMAIL_INPUT_ID = "group-unlock-email";

type GroupUnlockStatus =
  | "idle"
  | "editing"
  | "no-group"
  | "account-exists"
  | "error";

export const isGroupTicketType = (
  ticketType: Pick<TicketTypeItemType, "userType">
) => ticketType.userType === "group";

type GroupUnlockSessionUser = Pick<MyUserType, "isGuestCheckout"> | null | undefined;

// A session without its user loaded yet counts as a real account: the line must not flash
// for a logged-in visitor. An anonymous visitor has no session at all. Once Group tickets
// are listed, the unlock has already happened.
export const canUnlockGroupTickets = ({
  event,
  isAuthenticated,
  user,
  ticketTypes,
}: {
  event: Pick<PublicEventDetails, "hasGroupTicketTypes"> | undefined;
  isAuthenticated: boolean;
  user: GroupUnlockSessionUser;
  ticketTypes: Pick<TicketTypeItemType, "userType">[];
}): boolean => {
  const isRealAccount = isAuthenticated && (!user || !user.isGuestCheckout);

  return (
    !!event?.hasGroupTicketTypes &&
    !isRealAccount &&
    !ticketTypes.some(isGroupTicketType)
  );
};

// The underlined line itself, shared by the ticketing modal and the event page.
export const GroupUnlockLine = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-fit text-left text-sm font-medium text-primary-blue underline underline-offset-2"
  >
    {GROUP_UNLOCK_LINE}
  </button>
);

export type GroupUnlockedSession = {
  // Guest session token returned by the guest authentication (Bearer for the embed).
  accessToken: string;
  email: string;
};

type GroupUnlockProps = {
  event: PublicEventDetails;
  // The slug the page was loaded with: it drives the query the ticketing reads from.
  eventSlug?: string;
  // Embed: refetch the event with the guest token instead of relying on the session cookie.
  onUnlocked?: (session: GroupUnlockedSession) => Promise<void>;
  // The embed stays self-contained: no login call-to-action on account-exists.
  canLogin?: boolean;
  // The event page line opens the ticketing straight on the email field.
  defaultEditing?: boolean;
};

/**
 * Group unlock (ADR 0005): a visitor without an account types their email, which opens a
 * guest session; the public event is then refetched so the Group tickets they are a member
 * of show up. Nothing is verified here, eligibility is enforced by the server.
 */
export function GroupUnlock({
  event,
  eventSlug,
  onUnlocked,
  canLogin = true,
  defaultEditing = false,
}: GroupUnlockProps) {
  const queryClient = useQueryClient();
  const { refetchSession } = useUserContext();
  const { refetch: refetchUser } = useMyUserQuery();
  const { mutateAsync: authGuest, isPending } = useAuthGuest();

  const [status, setStatus] = useState<GroupUnlockStatus>(
    defaultEditing ? "editing" : "idle"
  );
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const emailInputRef = useRef<HTMLInputElement>(null);

  // The field takes the focus as soon as it is shown, whether the line was clicked here or
  // on the event page. The ticketing dialog lets this focus stand (see its onOpenAutoFocus).
  useEffect(() => {
    if (status === "editing") {
      emailInputRef.current?.focus();
    }
  }, [status]);

  const refreshAfterUnlock = async () => {
    refetchSession();
    await refetchUser();
    queryClient.invalidateQueries({ queryKey: basketQueryKeys.get("store") });
    queryClient.invalidateQueries({ queryKey: eventsQueryKeys.get(event.id) });
    // The event filter runs per session: the refetch is what reveals the Group tickets.
    const slug = eventSlug ?? event.slug;

    if (!slug) {
      return queryClient.fetchQuery({
        queryKey: eventsQueryKeys.get(event.id),
        queryFn: () => getEvent(event.id),
        staleTime: 0,
      });
    }
    return queryClient.fetchQuery({
      queryKey: eventsQueryKeys.getBySlug(slug),
      queryFn: () => getEventBySlug(slug),
      staleTime: 0,
    });
  };

  const handleSubmit = async (formEvent: FormEvent<HTMLFormElement>) => {
    formEvent.preventDefault();
    setErrorMessage("");

    const normalizedEmail = email.trim();
    let result: AuthGuestResponseDto;
    try {
      result = await authGuest({
        email: normalizedEmail,
        groupUnlockEventId: event.id,
      });
    } catch (error) {
      const isAccountExists =
        isAxiosError(error) &&
        error.response?.status === ACCOUNT_EXISTS_HTTP_STATUS;
      setErrorMessage(
        getErrorMessage(error, "Une erreur est survenue, veuillez réessayer.")
      );
      setStatus(isAccountExists ? "account-exists" : "error");
      return;
    }

    // The server already settled the outcome, and left nothing behind when it is a miss:
    // no session was opened, so there is no event to refetch.
    if (result.noGroupFound) {
      setStatus("no-group");

      return;
    }

    const { accessToken } = result;
    try {
      if (onUnlocked) {
        await onUnlocked({ accessToken, email: normalizedEmail });
      } else {
        await refreshAfterUnlock();
      }

      setStatus("editing");
    } catch (error) {
      setErrorMessage(
        getErrorMessage(error, "Une erreur est survenue, veuillez réessayer.")
      );
      setStatus("error");
    }
  };

  const handleStartEditing = () => {
    setStatus("editing");
  };

  // The visitor already typed the address the account belongs to: the login form starts with it.
  const handleOpenLogin = () => {
    AuthStore.openModal("LOGIN", { email });
  };

  if (status === "idle") {
    return <GroupUnlockLine onClick={handleStartEditing} />;
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4"
    >
      <label htmlFor={EMAIL_INPUT_ID} className="text-sm font-medium">
        Saisissez votre email pour afficher les billets réservés à votre groupe.
      </label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          ref={emailInputRef}
          id={EMAIL_INPUT_ID}
          type="email"
          name="email"
          required
          autoComplete="email"
          placeholder="votre@email.fr"
          value={email}
          onChange={(changeEvent) => setEmail(changeEvent.target.value)}
          disabled={isPending}
          className="bg-white"
        />
        <Button type="submit" variant="blue" isLoading={isPending}>
          Valider
        </Button>
      </div>
      {status === "no-group" && (
        <p className="text-sm text-gray-600">{GROUP_UNLOCK_NO_GROUP_MESSAGE}</p>
      )}
      {(status === "account-exists" || status === "error") && (
        <p className="text-sm text-red-500">{errorMessage}</p>
      )}
      {status === "account-exists" && canLogin && (
        <Button
          type="button"
          variant="third-blue"
          className="w-fit"
          onClick={handleOpenLogin}
        >
          Se connecter
        </Button>
      )}
    </form>
  );
}
